import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDSectionHeader } from '../primitives/CDSectionHeader';
import {
  CDClosingCostLineItem,
  CDClosingCostSubtotal,
  type ClosingCostLineItemData,
} from './CDClosingCostLineItem';

export interface OtherCostsSection {
  items: ClosingCostLineItemData[];
  subtotal?: number;
}

export interface OtherCostsData {
  taxesAndGovernmentFees: OtherCostsSection;
  prepaids: OtherCostsSection;
  initialEscrowPaymentAtClosing: OtherCostsSection;
  other: OtherCostsSection;
  totalOtherCosts: number;
}

export interface CDOtherCostsProps {
  /** Other costs data */
  data: Partial<OtherCostsData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<OtherCostsData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDOtherCosts - Other Costs section (E through I) on Page 2
 *
 * Contains:
 * - E. Taxes and Other Government Fees
 * - F. Prepaids
 * - G. Initial Escrow Payment at Closing
 * - H. Other
 * - I. TOTAL OTHER COSTS (Borrower-Paid)
 */
export const CDOtherCosts = React.forwardRef<HTMLDivElement, CDOtherCostsProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    const taxesItems = data.taxesAndGovernmentFees?.items || [];
    const prepaidsItems = data.prepaids?.items || [];
    const escrowItems = data.initialEscrowPaymentAtClosing?.items || [];
    const otherItems = data.other?.items || [];

    // Helper to update an item in a section
    const updateSectionItem = (
      section: 'taxesAndGovernmentFees' | 'prepaids' | 'initialEscrowPaymentAtClosing' | 'other',
      itemIndex: number,
      updatedItem: ClosingCostLineItemData,
    ) => {
      const sectionData = data[section] || { items: [] };
      const updatedItems = sectionData.items.map((item, idx) =>
        idx === itemIndex ? updatedItem : item,
      );
      onChange?.({
        ...data,
        [section]: { ...sectionData, items: updatedItems },
      });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Other Costs" />

        <table className="w-full border-collapse border-t-0">
          {/* Column Headers */}
          <thead>
            <tr className="cd-text-tiny">
              <th className="border border-t-0 border-cd-black px-1 py-0.5 w-6"></th>
              <th className="border border-t-0 border-cd-black px-1 py-0.5 text-left"></th>
              <th
                className="border border-t-0 border-cd-black px-1 py-0.5 text-center w-16"
                colSpan={2}
              >
                Borrower-Paid
              </th>
              <th
                className="border border-t-0 border-cd-black px-1 py-0.5 text-center w-16"
                colSpan={2}
              >
                Seller-Paid
              </th>
              <th className="border border-t-0 border-cd-black px-1 py-0.5 text-center w-16">
                Paid by
                <br />
                Others
              </th>
            </tr>
            <tr className="cd-text-tiny">
              <th className="border border-cd-black px-1 py-0.5"></th>
              <th className="border border-cd-black px-1 py-0.5"></th>
              <th className="border border-cd-black px-1 py-0.5 text-center w-16">At Closing</th>
              <th className="border border-cd-black px-1 py-0.5 text-center w-16">
                Before Closing
              </th>
              <th className="border border-cd-black px-1 py-0.5 text-center w-16">At Closing</th>
              <th className="border border-cd-black px-1 py-0.5 text-center w-16">
                Before Closing
              </th>
              <th className="border border-cd-black px-1 py-0.5 w-16"></th>
            </tr>
          </thead>

          <tbody>
            {/* E. Taxes and Other Government Fees */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-subsection-header">
                  <span className="cd-subsection-header-letter">
                    E. Taxes and Other Government Fees
                  </span>
                  <span className="cd-subsection-header-amount">
                    <CDCurrency
                      value={data.taxesAndGovernmentFees?.subtotal}
                      className="cd-text-small"
                    />
                  </span>
                </div>
              </td>
            </tr>
            {taxesItems.map((item, idx) => (
              <CDClosingCostLineItem
                key={idx}
                data={item}
                editable={editable}
                onChange={(updatedItem) =>
                  updateSectionItem('taxesAndGovernmentFees', idx, updatedItem)
                }
              />
            ))}

            {/* F. Prepaids */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-subsection-header">
                  <span className="cd-subsection-header-letter">F. Prepaids</span>
                  <span className="cd-subsection-header-amount">
                    <CDCurrency value={data.prepaids?.subtotal} className="cd-text-small" />
                  </span>
                </div>
              </td>
            </tr>
            {prepaidsItems.map((item, idx) => (
              <CDClosingCostLineItem
                key={idx}
                data={item}
                editable={editable}
                onChange={(updatedItem) => updateSectionItem('prepaids', idx, updatedItem)}
              />
            ))}

            {/* G. Initial Escrow Payment at Closing */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-subsection-header">
                  <span className="cd-subsection-header-letter">
                    G. Initial Escrow Payment at Closing
                  </span>
                  <span className="cd-subsection-header-amount">
                    <CDCurrency
                      value={data.initialEscrowPaymentAtClosing?.subtotal}
                      className="cd-text-small"
                    />
                  </span>
                </div>
              </td>
            </tr>
            {escrowItems.map((item, idx) => (
              <CDClosingCostLineItem
                key={idx}
                data={item}
                editable={editable}
                onChange={(updatedItem) =>
                  updateSectionItem('initialEscrowPaymentAtClosing', idx, updatedItem)
                }
              />
            ))}

            {/* H. Other */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-subsection-header">
                  <span className="cd-subsection-header-letter">H. Other</span>
                  <span className="cd-subsection-header-amount">
                    <CDCurrency value={data.other?.subtotal} className="cd-text-small" />
                  </span>
                </div>
              </td>
            </tr>
            {otherItems.map((item, idx) => (
              <CDClosingCostLineItem
                key={idx}
                data={item}
                editable={editable}
                onChange={(updatedItem) => updateSectionItem('other', idx, updatedItem)}
              />
            ))}

            {/* I. Total Other Costs */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-section-header flex justify-between items-center">
                  <span>I. TOTAL OTHER COSTS (Borrower-Paid)</span>
                  <span className="cd-text-small">Other Costs Subtotals (E + F + G + H)</span>
                </div>
              </td>
            </tr>
            <CDClosingCostSubtotal label="" borrowerTotal={data.totalOtherCosts} />
          </tbody>
        </table>
      </div>
    );
  },
);

CDOtherCosts.displayName = 'CDOtherCosts';
